import { FIELD_TYPES } from 'constants/fieldTypes';
import {
  SUCCESSION_POOL_FIELDS,
  SUCCESSION_POOL_MEMBER_FORM_MODE,
  SUCCESSION_POOL_MEMBER_FIELDS,
  SUCCESSION_USER_INDICATOR_KEYS,
} from 'constants/succession';
import { isSuccessionUserRemoved } from 'utility/succession';
import { formatDate } from 'utility/dateUtils';
import { isArray, isArrayEmpty, trimString } from 'utility/helpers';
import {
  forbiddenCharacters,
  isEmpty,
  validateLength,
} from 'utility/validation';

const { ADD, EDIT, REMOVE } = SUCCESSION_POOL_MEMBER_FORM_MODE;
const { TEXT, SELECT, PEOPLE_PICKER, USER_AVATAR, READ_ONLY } = FIELD_TYPES;
const NOTE_MAX_LENGTH = 1000;

const textColumn = (id, title, rowKey, minWidth, maxWidth = '1fr') => ({
  id,
  title,
  rowKey,
  isSortingDisabled: true,
  minWidth,
  maxWidth,
});

export const preparePoolMemberTableHeaders = labels => [
  textColumn('name', labels.name, 'name', 220),
  textColumn('jobLevel', labels.jobLevel, 'jobLevel', 180),
  textColumn('family', labels.family, 'family', 140),
  textColumn('readiness', labels.readiness, 'readiness', 140),
  textColumn('note', labels.note, 'note', 160, '1fr', false, true),
  textColumn('added', labels.added, 'added', 120, 160),
  textColumn('status', labels.status, 'statusComponent', 110, 140),
];

export const preparePoolMemberTableRows = (
  labels,
  members,
  settings,
  renderUser,
  renderActions,
  renderStatus
) =>
  (members || []).map((member, index) => {
    const { employee } = member;

    return {
      id: member.id || employee?.id || `member-${index}`,
      name: renderUser(employee),
      jobLevel: '-',
      family: employee?.job_family || '-',
      readiness: member?.readiness?.label || '-',
      note: member?.note || '-',
      added: formatDate(member?.created_at) || '-',
      status: member?.status,
      statusComponent: member?.status
        ? renderStatus({
            key: SUCCESSION_USER_INDICATOR_KEYS[member.status],
            name: labels?.statuses?.[member.status] || '-',
            value: member.status,
          })
        : '-',
      actions: renderActions(member),
    };
  });

export const preparePoolMemberFormFields = (mode, member) => {
  if (mode === REMOVE) {
    const isViewingRemoved = isSuccessionUserRemoved(member?.status);

    if (isViewingRemoved) {
      return [
        {
          name: 'member',
          type: USER_AVATAR,
          avatarProps: {
            variant: 'subtitle1',
            caption: true,
            small: true,
          },
        },
        {
          name: SUCCESSION_POOL_MEMBER_FIELDS.REASON,
          translationKey: 'reason',
          type: READ_ONLY,
          content: 'reasonLabel',
        },
        {
          name: SUCCESSION_POOL_MEMBER_FIELDS.COMMENT,
          translationKey: 'comment',
          type: READ_ONLY,
          content: 'removalComment',
        },
      ];
    }

    return [
      {
        name: 'member',
        type: USER_AVATAR,
        avatarProps: {
          variant: 'subtitle1',
          caption: true,
          small: true,
        },
      },
      {
        name: SUCCESSION_POOL_MEMBER_FIELDS.REASON,
        translationKey: 'reason',
        type: SELECT,
        optionsKey: 'removalReasons',
        isSearchDisabled: true,
        required: true,
        parser: { value: 'id', label: 'label' },
        validators: [{ type: 'required', validator: isEmpty }],
      },
      {
        name: SUCCESSION_POOL_MEMBER_FIELDS.COMMENT,
        type: TEXT,
        multiline: true,
        rows: 4,
        validators: [
          { type: 'forbiddenCharacters', validator: forbiddenCharacters },
          {
            type: 'maxLength',
            validator: value => validateLength(value || '', 0, NOTE_MAX_LENGTH),
          },
        ],
      },
    ];
  }

  const fields = [];

  if (mode === ADD) {
    fields.push({
      name: SUCCESSION_POOL_FIELDS.MEMBERS,
      type: PEOPLE_PICKER,
      optionsKey: 'employees',
      showSelection: true,
      shouldSortAlphabetically: true,
      shouldCalculateSelectionHeight: true,
      required: true,
      validators: [
        {
          type: 'required',
          validator: value => isArray(value) && !isArrayEmpty(value),
        },
      ],
    });
  } else {
    fields.push({
      name: 'member',
      type: USER_AVATAR,
      avatarProps: {
        variant: 'subtitle1',
        caption: true,
        small: true,
      },
    });
  }

  fields.push(
    {
      name: SUCCESSION_POOL_MEMBER_FIELDS.READINESS,
      translationKey: 'readiness',
      type: SELECT,
      optionsKey: 'readinessOptions',
      isSearchDisabled: true,
      required: true,
      parser: { value: 'id', label: 'label' },
      validators: [{ type: 'required', validator: isEmpty }],
    },
    {
      name: SUCCESSION_POOL_MEMBER_FIELDS.NOTE,
      type: TEXT,
      multiline: true,
      rows: 4,
      validators: [
        { type: 'forbiddenCharacters', validator: forbiddenCharacters },
        {
          type: 'maxLength',
          validator: value => validateLength(value || '', 0, NOTE_MAX_LENGTH),
        },
      ],
    }
  );

  return fields;
};

export const preparePoolMemberFormInitialData = (mode, member = {}) => {
  const { employee } = member;

  if (mode === REMOVE) {
    const removalComment = member?.comment || member?.removal_comment || '';

    return {
      id: member?.id,
      member: employee,
      [SUCCESSION_POOL_MEMBER_FIELDS.REASON]:
        member?.removal_reason?.id || null,
      [SUCCESSION_POOL_MEMBER_FIELDS.COMMENT]: removalComment,
      reasonLabel: member?.removal_reason?.label || '-',
      removalComment: removalComment || '-',
    };
  }

  if (mode === EDIT) {
    return {
      id: member?.id,
      member: employee,
      [SUCCESSION_POOL_MEMBER_FIELDS.READINESS]: member?.readiness?.id || null,
      [SUCCESSION_POOL_MEMBER_FIELDS.NOTE]: member?.note,
    };
  }

  return {
    [SUCCESSION_POOL_MEMBER_FIELDS.MEMBERS]: [],
    [SUCCESSION_POOL_MEMBER_FIELDS.READINESS]: null,
    [SUCCESSION_POOL_MEMBER_FIELDS.NOTE]: '',
  };
};

export const prepareAddMembersPayload = values => {
  return {
    [SUCCESSION_POOL_FIELDS.MEMBERS]: values[
      SUCCESSION_POOL_FIELDS.MEMBERS
    ].map(member => ({
      [SUCCESSION_POOL_MEMBER_FIELDS.EMPLOYEE]: member.id,
      [SUCCESSION_POOL_MEMBER_FIELDS.READINESS]:
        values[SUCCESSION_POOL_MEMBER_FIELDS.READINESS],
      [SUCCESSION_POOL_MEMBER_FIELDS.NOTE]: trimString(
        values[SUCCESSION_POOL_MEMBER_FIELDS.NOTE] || ''
      ),
    })),
  };
};

export const prepareEditMemberPayload = values => ({
  [SUCCESSION_POOL_MEMBER_FIELDS.READINESS]:
    values[SUCCESSION_POOL_MEMBER_FIELDS.READINESS],
  [SUCCESSION_POOL_MEMBER_FIELDS.NOTE]: trimString(
    values[SUCCESSION_POOL_MEMBER_FIELDS.NOTE] || ''
  ),
});

export const prepareRemoveMemberPayload = values => ({
  data: {
    [SUCCESSION_POOL_MEMBER_FIELDS.REASON]:
      values[SUCCESSION_POOL_MEMBER_FIELDS.REASON],
    [SUCCESSION_POOL_MEMBER_FIELDS.COMMENT]: trimString(
      values[SUCCESSION_POOL_MEMBER_FIELDS.COMMENT] || ''
    ),
  },
});

export const prepareAvailableMembers = (allUsers, members) => {
  const availabelMemberIds = new Set(
    (members || []).reduce((acc, member) => {
      if (isSuccessionUserRemoved(member?.status)) {
        return acc;
      }
      return [...acc, String(member?.employee?.id)];
    }, [])
  );

  return (allUsers || []).filter(
    user => !availabelMemberIds.has(String(user?.id))
  );
};
